/**
 * Bottom install prompt for installable PWAs (Chrome/Edge/Android Chromium).
 * Uses beforeinstallprompt; respects dismiss & successful install per app version.
 * See: https://developer.chrome.com/blog/richer-pwa-installation
 *      https://web.dev/learn/pwa/installation
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import packageJson from '../../package.json';
import './InstallAppBanner.css';

const APP_VERSION = packageJson.version;
const STORAGE_DISMISS = 'cranfordPwaInstallDismissVer';
const STORAGE_INSTALLED = 'cranfordPwaInstallInstalledVer';

function isStandaloneDisplay() {
  if (typeof window === 'undefined') return true;
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    window.matchMedia('(display-mode: window-controls-overlay)').matches ||
    window.navigator.standalone === true
  );
}

function shouldSuppressForVersion() {
  try {
    const dismissed = localStorage.getItem(STORAGE_DISMISS);
    const installed = localStorage.getItem(STORAGE_INSTALLED);
    if (dismissed === APP_VERSION || installed === APP_VERSION) return true;
  } catch {
    /* ignore */
  }
  return false;
}

export default function InstallAppBanner() {
  const location = useLocation();
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [visible, setVisible] = useState(false);

  const hideForVersion = useCallback((storageKey) => {
    try {
      localStorage.setItem(storageKey, APP_VERSION);
    } catch {
      /* ignore */
    }
    setVisible(false);
    setDeferredPrompt(null);
  }, []);

  useEffect(() => {
    if (isStandaloneDisplay()) return undefined;
    if (location.pathname.startsWith('/admin')) return undefined;
    if (shouldSuppressForVersion()) return undefined;

    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setVisible(true);
    };

    const onAppInstalled = () => {
      hideForVersion(STORAGE_INSTALLED);
    };

    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    window.addEventListener('appinstalled', onAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      window.removeEventListener('appinstalled', onAppInstalled);
    };
  }, [location.pathname, hideForVersion]);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    try {
      await deferredPrompt.userChoice;
    } catch {
      /* ignore */
    }
    setDeferredPrompt(null);
    setVisible(false);
  };

  const handleDismiss = () => {
    hideForVersion(STORAGE_DISMISS);
  };

  if (!visible || !deferredPrompt) return null;

  return (
    <div className="install-app-banner" role="region" aria-label="Install web app">
      <div className="install-app-banner-inner">
        <p className="install-app-banner-text">
          <strong>Install Cranford Publications</strong>
          <span>Open quickly from your home screen like an app (offline-friendly shell).</span>
        </p>
        <div className="install-app-banner-actions">
          <button type="button" className="install-app-banner-install" onClick={handleInstall}>
            Install
          </button>
          <button type="button" className="install-app-banner-later" onClick={handleDismiss}>
            Not now
          </button>
        </div>
      </div>
    </div>
  );
}
